/*
  MSD — homepage hero background concepts (canvas).

  Selected by window.__msdHeroStyle (set by sections/animated-hero.liquid from the
  section setting, overridable with ?hero=board|scope|schematic|mesh):

    board      "Silkscreen board" — procedurally routed PCB traces on a dot grid,
               pads / vias / footprints / reference designators, signal pulses
               (teal analog dots, amber digital bits), and a probe spotlight that
               follows the cursor and lights the copper near it.
    scope      "Oscilloscope" — two-channel scope: CH1 an evolving analog wave on
               top, CH2 its sampled, quantised digital twin at the bottom, with
               graticule, readouts and sample ticks. Analog in, digital out.
    schematic  "Living schematic" — an analog-to-digital signal chain drawn like
               a real schematic sheet (source, RC, op-amp, ADC, 8-bit bus, MCU)
               with current flowing through it and the waveform changing shape
               at each stage.

  Shared rules: the copy box is a keep-out zone (nothing busy under the text),
  prefers-reduced-motion gets a static frame, the loop pauses off-screen, and
  everything is DPR-aware.

  Wiring: <script src="msd-hero-concepts.js" defer data-canvas="<canvas id>" data-keepout="<selector>">
*/
(function () {
  var script = document.currentScript;
  if (!script) return;
  var style = window.__msdHeroStyle || 'board';
  if (style === 'mesh') return;

  var canvas = document.getElementById(script.getAttribute('data-canvas'));
  if (!canvas) return;
  var keepoutSel = script.getAttribute('data-keepout');
  var ctx = canvas.getContext('2d');
  var reduceMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  var TEAL = '62, 207, 207', CREAM = '237, 232, 213', AMBER = '196, 122, 58', BG = '#0B0A08';
  var MONO = '"Space Mono", "Courier New", monospace';
  var W = 0, H = 0, DPR = 1, mobile = false, visible = true, raf = 0, lastT = 0, t0 = 0;
  var mouse = { x: -9999, y: -9999, active: false };
  var keepout = null;

  var seed = 1337;
  function rnd() { seed = (seed * 16807) % 2147483647; return (seed - 1) / 2147483646; }
  function pick(a) { return a[Math.floor(rnd() * a.length)]; }
  function easeOut(x) { return 1 - Math.pow(1 - x, 3); }
  function clamp(v, a, b) { return Math.max(a, Math.min(b, v)); }

  function measureKeepout(margin) {
    var box = keepoutSel && document.querySelector(keepoutSel);
    if (!box) return null;
    var cr = canvas.getBoundingClientRect(), br = box.getBoundingClientRect();
    return { x0: br.left - cr.left - margin, y0: br.top - cr.top - margin, x1: br.right - cr.left + margin, y1: br.bottom - cr.top + margin };
  }
  function dotGrid(o, g, alpha) {
    o.fillStyle = 'rgba(' + CREAM + ', ' + alpha + ')';
    for (var y = g / 2; y < H; y += g) for (var x = g / 2; x < W; x += g) o.fillRect(x - 0.5, y - 0.5, 1, 1);
  }
  function label(o, text, x, y, alpha, size, align) {
    o.font = '700 ' + (size || 9) + 'px ' + MONO;
    o.textAlign = align || 'left'; o.textBaseline = 'middle';
    o.fillStyle = 'rgba(' + CREAM + ', ' + alpha + ')';
    o.fillText(text, x, y);
    o.textAlign = 'left';
  }

  /* ════════════════════════════════════════════════════════════════════
     CONCEPT 1 — SILKSCREEN BOARD
     ════════════════════════════════════════════════════════════════════ */
  var Board = (function () {
    var traces = [], pulses = [], base = null, bright = null, drawStart = 0, DRAW_MS = 2400, g = 26;

    function route() {
      seed = 1337; traces = [];
      g = mobile ? 18 : 20;
      var cols = Math.ceil(W / g), rows = Math.ceil(H / g);
      var occ = new Uint8Array(cols * rows);
      var at = function (c, r) { return (c < 1 || r < 1 || c >= cols - 1 || r >= rows - 1) ? 1 : occ[r * cols + c]; };
      var mark = function (c, r) { if (c >= 0 && r >= 0 && c < cols && r < rows) occ[r * cols + c] = 1; };
      var ko = keepout;
      if (ko) for (var r = 0; r < rows; r++) for (var c = 0; c < cols; c++) {
        var px = c * g, py = r * g;
        if (px > ko.x0 && px < ko.x1 && py > ko.y0 && py < ko.y1) mark(c, r);
      }
      // corner mounting holes are keep-outs too
      [[2, 2], [cols - 3, 2], [2, rows - 3], [cols - 3, rows - 3]].forEach(function (h) { for (var i = -1; i <= 1; i++) for (var j = -1; j <= 1; j++) mark(h[0] + i, h[1] + j); });

      function tryPath(c0, r0, c1, r1) {
        var cells = [{ c: c0, r: r0 }], c = c0, r = r0;
        var dc = Math.sign(c1 - c0), dr = Math.sign(r1 - r0), horizFirst = rnd() < 0.5, steps = 0;
        function step(nc, nr) {
          if (at(nc, nr)) return false;
          for (var i = -1; i <= 1; i++) for (var j = -1; j <= 1; j++) {
            var cc = nc + i, rr = nr + j;
            if (cc === c && rr === r) continue;
            if (cells.length > 1 && cc === cells[cells.length - 2].c && rr === cells[cells.length - 2].r) continue;
            if (at(cc, rr)) return false;
          }
          c = nc; r = nr; cells.push({ c: c, r: r }); return true;
        }
        var diag = Math.min(Math.abs(c1 - c0), Math.abs(r1 - r0));
        var diagLen = Math.floor(diag * (0.3 + rnd() * 0.5));
        var straightA = (horizFirst ? Math.abs(c1 - c0) : Math.abs(r1 - r0)) - diagLen;
        for (var i = 0; i < straightA; i++) { if (!(horizFirst ? step(c + dc, r) : step(c, r + dr))) return null; if (++steps > 300) return null; }
        for (var d = 0; d < diagLen; d++) if (!step(c + dc, r + dr)) return null;
        while (c !== c1 || r !== r1) {
          var nc = c, nr = r;
          if (horizFirst) { if (r !== r1) nr = r + dr; else nc = c + dc; } else { if (c !== c1) nc = c + dc; else nr = r + dr; }
          if (!step(nc, nr)) return null;
          if (++steps > 500) return null;
        }
        return cells;
      }

      var target = clamp(Math.round((W * H) / (mobile ? 9000 : 11000)), 16, 90), attempts = 0;
      while (traces.length < target && attempts < 4000) {
        attempts++;
        var c0 = 2 + Math.floor(rnd() * (cols - 4)), r0 = 2 + Math.floor(rnd() * (rows - 4));
        if (at(c0, r0)) continue;
        var lenC = Math.floor(2 + rnd() * Math.min(20, cols * 0.45)) * (rnd() < 0.5 ? 1 : -1);
        var lenR = Math.floor(1 + rnd() * Math.min(12, rows * 0.6)) * (rnd() < 0.5 ? 1 : -1);
        if (rnd() < 0.2) lenR = 0;
        if (rnd() < 0.2) lenC = Math.sign(lenC) * 2;
        var c1 = clamp(c0 + lenC, 2, cols - 3), r1 = clamp(r0 + lenR, 2, rows - 3);
        if (Math.abs(c1 - c0) + Math.abs(r1 - r0) < 3 || at(c1, r1)) continue;
        var cells = tryPath(c0, r0, c1, r1);
        if (!cells) continue;
        cells.forEach(function (cl) { mark(cl.c, cl.r); });
        var pts = [];
        for (var i = 0; i < cells.length; i++) {
          if (i > 0 && i < cells.length - 1) {
            var a = cells[i - 1], b = cells[i + 1];
            if ((cells[i].c - a.c) === (b.c - cells[i].c) && (cells[i].r - a.r) === (b.r - cells[i].r)) continue;
          }
          pts.push({ x: cells[i].c * g, y: cells[i].r * g });
        }
        var cum = [0], len = 0;
        for (var k = 1; k < pts.length; k++) { len += Math.hypot(pts[k].x - pts[k - 1].x, pts[k].y - pts[k - 1].y); cum.push(len); }
        traces.push({
          pts: pts, len: len, cum: cum,
          startKind: rnd() < 0.55 ? 'pad' : 'via',
          endKind: rnd() < 0.4 ? 'part' : (rnd() < 0.5 ? 'pad' : 'via'),
          label: rnd() < 0.5 ? pick(['C', 'R', 'U', 'L', 'D', 'J', 'Q']) + Math.floor(1 + rnd() * 48) : null,
          delay: rnd() * 0.55,
          horizontalEnd: pts.length > 1 && pts[pts.length - 1].y === pts[pts.length - 2].y
        });
      }
    }

    function pointAt(t, d) {
      d = clamp(d, 0, t.len);
      for (var i = 1; i < t.pts.length; i++) if (d <= t.cum[i]) {
        var seg = t.cum[i] - t.cum[i - 1], f = seg ? (d - t.cum[i - 1]) / seg : 0;
        return { x: t.pts[i - 1].x + (t.pts[i].x - t.pts[i - 1].x) * f, y: t.pts[i - 1].y + (t.pts[i].y - t.pts[i - 1].y) * f };
      }
      var l = t.pts[t.pts.length - 1]; return { x: l.x, y: l.y };
    }
    function strokePartial(o, t, drawLen) {
      o.beginPath(); o.moveTo(t.pts[0].x, t.pts[0].y);
      for (var i = 1; i < t.pts.length; i++) {
        if (t.cum[i] <= drawLen) o.lineTo(t.pts[i].x, t.pts[i].y);
        else { var p = pointAt(t, drawLen); o.lineTo(p.x, p.y); break; }
      }
      o.stroke();
    }
    function terminal(o, p, kind, t, k) {
      if (kind === 'pad') {
        o.fillStyle = 'rgba(' + TEAL + ', ' + (0.55 * k) + ')'; o.beginPath(); o.arc(p.x, p.y, 3.2, 0, Math.PI * 2); o.fill();
        o.strokeStyle = 'rgba(' + TEAL + ', ' + (0.35 * k) + ')'; o.lineWidth = 1; o.beginPath(); o.arc(p.x, p.y, 5.5, 0, Math.PI * 2); o.stroke();
      } else if (kind === 'via') {
        o.strokeStyle = 'rgba(' + TEAL + ', ' + (0.6 * k) + ')'; o.lineWidth = 1.5; o.beginPath(); o.arc(p.x, p.y, 3, 0, Math.PI * 2); o.stroke();
        o.fillStyle = BG; o.beginPath(); o.arc(p.x, p.y, 1.4, 0, Math.PI * 2); o.fill();
      } else {
        var horiz = t.horizontalEnd, w = horiz ? 22 : 10, h = horiz ? 10 : 22;
        o.strokeStyle = 'rgba(' + CREAM + ', ' + (0.32 * k) + ')'; o.lineWidth = 1; o.strokeRect(p.x - w / 2, p.y - h / 2, w, h);
        o.fillStyle = 'rgba(' + TEAL + ', ' + (0.5 * k) + ')';
        if (horiz) { o.fillRect(p.x - w / 2 + 2, p.y - 3, 5, 6); o.fillRect(p.x + w / 2 - 7, p.y - 3, 5, 6); }
        else { o.fillRect(p.x - 3, p.y - h / 2 + 2, 6, 5); o.fillRect(p.x - 3, p.y + h / 2 - 7, 6, 5); }
      }
    }

    function render(progress, k, withChrome) {
      // k = brightness multiplier (1 = base copper, ~2.2 = lit by the probe)
      var off = document.createElement('canvas');
      off.width = Math.round(W * DPR); off.height = Math.round(H * DPR);
      var o = off.getContext('2d'); o.setTransform(DPR, 0, 0, DPR, 0, 0);
      o.fillStyle = BG; o.fillRect(0, 0, W, H);
      dotGrid(o, g, 0.06 * Math.min(k, 1.4));
      if (withChrome) {
        // board outline + mounting holes + title silkscreen
        o.strokeStyle = 'rgba(' + CREAM + ', 0.10)'; o.lineWidth = 1; o.strokeRect(g, g, W - 2 * g, H - 2 * g);
        [[2 * g, 2 * g], [W - 2 * g, 2 * g], [2 * g, H - 2 * g], [W - 2 * g, H - 2 * g]].forEach(function (h) {
          o.strokeStyle = 'rgba(' + CREAM + ', 0.28)'; o.lineWidth = 1;
          o.beginPath(); o.arc(h[0], h[1], 7, 0, Math.PI * 2); o.stroke();
          o.beginPath(); o.arc(h[0], h[1], 3, 0, Math.PI * 2); o.stroke();
          o.beginPath(); o.moveTo(h[0] - 11, h[1]); o.lineTo(h[0] + 11, h[1]); o.moveTo(h[0], h[1] - 11); o.lineTo(h[0], h[1] + 11); o.stroke();
        });
        if (!mobile) {
          label(o, 'MSD  ·  K-SERIES  ·  REV C', 2.4 * g, H - 1.15 * g, 0.3, 9);
          label(o, 'DESIGNED + PACKED IN SWITZERLAND', W - 2.4 * g, H - 1.15 * g, 0.3, 9, 'right');
        }
      }
      o.lineCap = 'round'; o.lineJoin = 'round';
      traces.forEach(function (t) {
        var local = clamp((progress - t.delay) / 0.45, 0, 1);
        if (local <= 0) return;
        var drawLen = t.len * easeOut(local);
        o.strokeStyle = 'rgba(' + TEAL + ', ' + Math.min(0.95, 0.34 * k) + ')'; o.lineWidth = k > 1 ? 1.8 : 1.5;
        strokePartial(o, t, drawLen);
        if (local < 1) { var hp = pointAt(t, drawLen); o.fillStyle = 'rgba(' + TEAL + ', 0.95)'; o.beginPath(); o.arc(hp.x, hp.y, 2.2, 0, Math.PI * 2); o.fill(); }
        terminal(o, t.pts[0], t.startKind, t, Math.min(1.7, k));
        if (local >= 1) {
          terminal(o, t.pts[t.pts.length - 1], t.endKind, t, Math.min(1.7, k));
          if (t.label) { var lp = t.pts[t.pts.length - 1]; label(o, t.label, lp.x + (t.endKind === 'part' ? 14 : 8), lp.y - 9, Math.min(0.9, 0.36 * k), 9); }
        }
      });
      return off;
    }

    function spawnPulse() {
      var t = pick(traces), digital = rnd() < 0.35;
      pulses.push({ t: t, d: 0, dir: rnd() < 0.5 ? 1 : -1, speed: digital ? 150 + rnd() * 90 : 90 + rnd() * 70, digital: digital, life: 0 });
    }
    function drawPulses(dt) {
      var want = clamp(Math.round(traces.length * 0.3), 4, 12);
      if (pulses.length < want && rnd() < 0.06) spawnPulse();
      for (var i = pulses.length - 1; i >= 0; i--) {
        var p = pulses[i]; p.d += p.speed * dt; p.life += dt;
        if (p.d > p.t.len) { pulses.splice(i, 1); continue; }
        var dist = p.dir === 1 ? p.d : p.t.len - p.d, head = pointAt(p.t, dist);
        var fade = Math.min(1, p.life * 3) * Math.min(1, (p.t.len - p.d) / 24);
        if (p.digital) {
          var tail = pointAt(p.t, dist - p.dir * 14);
          ctx.strokeStyle = 'rgba(' + AMBER + ', ' + (0.95 * fade) + ')'; ctx.lineWidth = 2.5; ctx.lineCap = 'butt';
          ctx.beginPath(); ctx.moveTo(tail.x, tail.y); ctx.lineTo(head.x, head.y); ctx.stroke();
          ctx.fillStyle = 'rgba(' + AMBER + ', ' + (0.35 * fade) + ')'; ctx.beginPath(); ctx.arc(head.x, head.y, 5, 0, Math.PI * 2); ctx.fill();
        } else {
          var tl = pointAt(p.t, dist - p.dir * 40), grad = ctx.createLinearGradient(tl.x, tl.y, head.x, head.y);
          grad.addColorStop(0, 'rgba(' + TEAL + ', 0)'); grad.addColorStop(1, 'rgba(' + TEAL + ', ' + (0.7 * fade) + ')');
          ctx.strokeStyle = grad; ctx.lineWidth = 2; ctx.lineCap = 'round';
          ctx.beginPath(); ctx.moveTo(tl.x, tl.y); ctx.lineTo(head.x, head.y); ctx.stroke();
          var rg = ctx.createRadialGradient(head.x, head.y, 0, head.x, head.y, 9);
          rg.addColorStop(0, 'rgba(' + TEAL + ', ' + (0.95 * fade) + ')'); rg.addColorStop(0.35, 'rgba(' + TEAL + ', ' + (0.45 * fade) + ')'); rg.addColorStop(1, 'rgba(' + TEAL + ', 0)');
          ctx.fillStyle = rg; ctx.beginPath(); ctx.arc(head.x, head.y, 9, 0, Math.PI * 2); ctx.fill();
        }
      }
    }

    return {
      setup: function () { route(); pulses = []; base = null; bright = null; drawStart = performance.now(); },
      frame: function (ts, dt) {
        var prog = reduceMotion ? 1 : clamp((ts - drawStart) / DRAW_MS, 0, 1);
        if (prog < 1 || !base) base = render(prog, 1, true);
        if (prog >= 1 && !bright) bright = render(1, 2.4, true);
        ctx.setTransform(1, 0, 0, 1, 0, 0); ctx.drawImage(base, 0, 0); ctx.setTransform(DPR, 0, 0, DPR, 0, 0);
        if (prog >= 1 && bright && mouse.active) {
          // probe spotlight: bright copper inside a soft circle around the cursor
          var R = mobile ? 90 : 170;
          ctx.save(); ctx.beginPath(); ctx.arc(mouse.x, mouse.y, R, 0, Math.PI * 2); ctx.clip();
          ctx.setTransform(1, 0, 0, 1, 0, 0); ctx.drawImage(bright, 0, 0); ctx.setTransform(DPR, 0, 0, DPR, 0, 0);
          var vg = ctx.createRadialGradient(mouse.x, mouse.y, R * 0.55, mouse.x, mouse.y, R);
          vg.addColorStop(0, 'rgba(11,10,8,0)'); vg.addColorStop(1, 'rgba(11,10,8,0.85)');
          ctx.fillStyle = vg; ctx.fillRect(mouse.x - R, mouse.y - R, 2 * R, 2 * R);
          ctx.restore();
          ctx.strokeStyle = 'rgba(' + TEAL + ', 0.22)'; ctx.lineWidth = 1;
          ctx.beginPath(); ctx.arc(mouse.x, mouse.y, R * 0.62, 0, Math.PI * 2); ctx.stroke();
        }
        if (!reduceMotion && prog >= 1) drawPulses(dt);
        return !reduceMotion;
      }
    };
  })();

  /* ════════════════════════════════════════════════════════════════════
     CONCEPT 2 — OSCILLOSCOPE (analog in, digital out)
     ════════════════════════════════════════════════════════════════════ */
  var Scope = (function () {
    var grat = null, N = 8, seen = 0;
    var ch1 = { y: 0, amp: 0 }, ch2 = { y: 0, amp: 0 };
    var glyphs = [], persist = null, samplePeriod = 48;

    function analog(x, t) {
      var k = 2 * Math.PI / (mobile ? 260 : 420);
      return 0.62 * Math.sin(k * x - t * 1.6) + 0.28 * Math.sin(k * 2.7 * x + t * 2.3 + Math.sin(t * 0.4) * 2) + 0.14 * Math.sin(k * 6.1 * x - t * 3.9);
    }
    function layout() {
      var koTop = keepout ? keepout.y0 : H * 0.35, koBot = keepout ? keepout.y1 : H * 0.65;
      var topBand = Math.max(60, koTop), botBand = Math.max(60, H - koBot);
      ch1.y = topBand * 0.55; ch1.amp = clamp(topBand * 0.30, 16, 70);
      ch2.y = koBot + botBand * 0.5; ch2.amp = clamp(botBand * 0.28, 14, 60);
      samplePeriod = mobile ? 30 : 44;
      var mk = document.createElement('canvas'); mk.width = Math.round(W * DPR); mk.height = Math.round(H * DPR);
      var o = mk.getContext('2d'); o.setTransform(DPR, 0, 0, DPR, 0, 0);
      o.fillStyle = BG; o.fillRect(0, 0, W, H);
      // graticule: 10 x 8 divisions, hairlines, ticks on the centre lines
      var dx = W / 10, dy = H / 8;
      o.strokeStyle = 'rgba(' + CREAM + ', 0.07)'; o.lineWidth = 1;
      for (var i = 1; i < 10; i++) { o.beginPath(); o.moveTo(Math.round(i * dx) + 0.5, 0); o.lineTo(Math.round(i * dx) + 0.5, H); o.stroke(); }
      for (var j = 1; j < 8; j++) { o.beginPath(); o.moveTo(0, Math.round(j * dy) + 0.5); o.lineTo(W, Math.round(j * dy) + 0.5); o.stroke(); }
      o.strokeStyle = 'rgba(' + CREAM + ', 0.16)';
      for (var x = 0; x < W; x += dx / 5) { o.beginPath(); o.moveTo(x, H / 2 - 3); o.lineTo(x, H / 2 + 3); o.stroke(); }
      for (var y = 0; y < H; y += dy / 5) { o.beginPath(); o.moveTo(W / 2 - 3, y); o.lineTo(W / 2 + 3, y); o.stroke(); }
      dotGrid(o, mobile ? 22 : 26, 0.045);
      // channel baselines + readouts
      o.setLineDash([2, 6]); o.strokeStyle = 'rgba(' + TEAL + ', 0.22)';
      o.beginPath(); o.moveTo(0, ch1.y + 0.5); o.lineTo(W, ch1.y + 0.5); o.stroke();
      o.strokeStyle = 'rgba(' + AMBER + ', 0.22)';
      o.beginPath(); o.moveTo(0, ch2.y + 0.5); o.lineTo(W, ch2.y + 0.5); o.stroke();
      o.setLineDash([]);
      var pad = mobile ? 14 : 28;
      label(o, 'CH1  ANALOG IN   1 V/DIV', pad, 18, 0.42, mobile ? 8 : 10);
      label(o, 'CH2  ADC OUT   8 BIT   ' + (mobile ? '' : 'FS = 10 kS/s'), pad, H - 16, 0.42, mobile ? 8 : 10);
      if (!mobile) {
        label(o, 'T  2 ms/DIV', W - pad, 18, 0.42, 10, 'right');
        label(o, 'TRIG  ↑  0.00 V', W - pad, H - 16, 0.42, 10, 'right');
      }
      // trigger marker on the right edge of CH1
      o.fillStyle = 'rgba(' + TEAL + ', 0.7)';
      o.beginPath(); o.moveTo(W, ch1.y - 5); o.lineTo(W - 7, ch1.y); o.lineTo(W, ch1.y + 5); o.closePath(); o.fill();
      grat = mk;
      persist = document.createElement('canvas'); persist.width = mk.width; persist.height = mk.height;
    }

    return {
      setup: function () { layout(); seen = 0; },
      frame: function (ts, dt) {
        var t = (ts - t0) / 1000;
        var pc = persist.getContext('2d'); pc.setTransform(DPR, 0, 0, DPR, 0, 0);
        // phosphor persistence: fade the previous traces instead of clearing
        pc.globalCompositeOperation = 'destination-out';
        pc.fillStyle = 'rgba(0,0,0,' + (reduceMotion ? 1 : 0.28) + ')'; pc.fillRect(0, 0, W, H);
        pc.globalCompositeOperation = 'source-over';

        // CH1 analog: glow pass + core pass
        var step = 3;
        function tracePath(o) { o.beginPath(); for (var x = 0; x <= W; x += step) { var y = ch1.y - analog(x, t) * ch1.amp; if (x === 0) o.moveTo(x, y); else o.lineTo(x, y); } }
        pc.lineJoin = 'round'; pc.lineCap = 'round';
        pc.strokeStyle = 'rgba(' + TEAL + ', 0.16)'; pc.lineWidth = 7; tracePath(pc); pc.stroke();
        pc.strokeStyle = 'rgba(' + TEAL + ', 0.95)'; pc.lineWidth = 1.6; tracePath(pc); pc.stroke();

        // sampling: ticks at fixed sample clock, quantised to 8 levels for the staircase
        var levels = 12, prevY = null;
        var stairs = [];
        for (var sx = samplePeriod / 2; sx <= W; sx += samplePeriod) {
          var v = analog(sx, t), q = Math.round(v * (levels / 2)) / (levels / 2);
          stairs.push({ x: sx, v: v, q: q });
        }
        // sample markers on CH1 (small squares) + drop lines to the baseline
        pc.fillStyle = 'rgba(' + AMBER + ', 0.9)'; pc.strokeStyle = 'rgba(' + AMBER + ', 0.25)'; pc.lineWidth = 1;
        stairs.forEach(function (s) {
          var y = ch1.y - s.v * ch1.amp;
          pc.beginPath(); pc.moveTo(s.x + 0.5, y); pc.lineTo(s.x + 0.5, ch1.y); pc.stroke();
          pc.fillRect(s.x - 2, y - 2, 4, 4);
        });
        // CH2 digital: staircase reconstruction (zero-order hold) + bit words
        pc.strokeStyle = 'rgba(' + AMBER + ', 0.18)'; pc.lineWidth = 6; pc.lineJoin = 'miter'; pc.lineCap = 'butt';
        function stairPath(o) {
          o.beginPath();
          stairs.forEach(function (s, i) {
            var y = ch2.y - s.q * ch2.amp;
            if (i === 0) o.moveTo(0, y);
            o.lineTo(s.x, y);
            var n = stairs[i + 1]; if (n) o.lineTo(s.x, ch2.y - n.q * ch2.amp);
          });
        }
        stairPath(pc); pc.stroke();
        pc.strokeStyle = 'rgba(' + AMBER + ', 0.95)'; pc.lineWidth = 1.5; stairPath(pc); pc.stroke();
        // bit words under every other sample: 8 tiny cells, lit = 1
        if (!mobile && ch2.y + ch2.amp + 20 < H - 8) {
          for (var i = 0; i < stairs.length; i += 2) {
            var s = stairs[i], code = clamp(Math.round((s.q + 1) / 2 * 255), 0, 255);
            if (s.x < 250 || s.x > W - 250) continue;
            for (var b = 0; b < 8; b++) {
              var on = (code >> (7 - b)) & 1;
              pc.fillStyle = on ? 'rgba(' + AMBER + ', 0.85)' : 'rgba(' + CREAM + ', 0.10)';
              pc.fillRect(s.x - 12 + b * 3.2, ch2.y + ch2.amp + 12, 2.2, 5);
            }
          }
        }
        // scanning sample clock cursor
        var cx = ((t * 140) % (W + 60)) - 30;
        pc.strokeStyle = 'rgba(' + CREAM + ', 0.35)'; pc.lineWidth = 1; pc.setLineDash([3, 5]);
        pc.beginPath(); pc.moveTo(cx + 0.5, 0); pc.lineTo(cx + 0.5, H); pc.stroke(); pc.setLineDash([]);

        ctx.setTransform(1, 0, 0, 1, 0, 0); ctx.drawImage(grat, 0, 0); ctx.drawImage(persist, 0, 0); ctx.setTransform(DPR, 0, 0, DPR, 0, 0);
        return !reduceMotion;
      }
    };
  })();

  /* ════════════════════════════════════════════════════════════════════
     CONCEPT 3 — LIVING SCHEMATIC (analog → digital signal chain)
     ════════════════════════════════════════════════════════════════════ */
  var Schematic = (function () {
    var sheet = null, wire = [], nodes = {}, y = 0, s = 1, carriers = [];

    function build() {
      // the chain lives in the band below the copy (or above it when there is more room)
      var koBot = keepout ? keepout.y1 : H * 0.7, koTop = keepout ? keepout.y0 : H * 0.3;
      var below = H - koBot, above = koTop;
      var band = below >= above ? { y0: koBot, y1: H } : { y0: 0, y1: koTop };
      y = band.y0 + (band.y1 - band.y0) * 0.52;
      s = clamp((band.y1 - band.y0) / 150, 0.55, 1.15);
      var m = mobile ? 14 : 60, x = m, span = W - 2 * m;
      // node x positions across the width
      nodes = { src: x + span * 0.03, r1: x + span * 0.16, opamp: x + span * 0.34, c1: x + span * 0.47, adc: x + span * 0.62, bus: x + span * 0.76, mcu: x + span * 0.92 };
      var mk = document.createElement('canvas'); mk.width = Math.round(W * DPR); mk.height = Math.round(H * DPR);
      var o = mk.getContext('2d'); o.setTransform(DPR, 0, 0, DPR, 0, 0);
      o.fillStyle = BG; o.fillRect(0, 0, W, H);
      dotGrid(o, mobile ? 22 : 26, 0.06);
      // sheet frame + title block, like a real schematic page
      o.strokeStyle = 'rgba(' + CREAM + ', 0.12)'; o.lineWidth = 1; o.strokeRect(10.5, 10.5, W - 21, H - 21);
      if (!mobile) {
        var tbw = 400, tbh = 44, tbx = W - 11 - tbw, tby = H - 11 - tbh, col = 250;
        o.strokeRect(tbx + 0.5, tby + 0.5, tbw, tbh);
        o.beginPath(); o.moveTo(tbx, tby + 22); o.lineTo(tbx + tbw, tby + 22); o.moveTo(tbx + col, tby); o.lineTo(tbx + col, tby + tbh); o.stroke();
        label(o, 'MIXED SIGNAL DEVELOPMENT', tbx + 10, tby + 11, 0.5, 9);
        label(o, 'SHEET 1 / 1', tbx + col + 10, tby + 11, 0.5, 9);
        label(o, 'ANALOG FRONT END → ADC → MCU', tbx + 10, tby + 33, 0.5, 9);
        label(o, 'REV C  ·  SWITZERLAND', tbx + col + 10, tby + 33, 0.5, 9);
      }
      o.lineWidth = 1.5; o.lineJoin = 'round'; o.lineCap = 'round';
      var C = 'rgba(' + CREAM + ', 0.55)', T = 'rgba(' + TEAL + ', 0.85)';
      function stroke(col) { o.strokeStyle = col; o.stroke(); }
      function wireTo(x0, x1) { o.beginPath(); o.moveTo(x0, y); o.lineTo(x1, y); stroke('rgba(' + TEAL + ', 0.5)'); }
      function junction(x) { o.fillStyle = T; o.beginPath(); o.arc(x, y, 3, 0, Math.PI * 2); o.fill(); }
      function ground(x, yy) { o.beginPath(); o.moveTo(x, yy); o.lineTo(x, yy + 10 * s); o.moveTo(x - 9 * s, yy + 10 * s); o.lineTo(x + 9 * s, yy + 10 * s); o.moveTo(x - 5.5 * s, yy + 15 * s); o.lineTo(x + 5.5 * s, yy + 15 * s); o.moveTo(x - 2 * s, yy + 20 * s); o.lineTo(x + 2 * s, yy + 20 * s); stroke(C); }
      // source: circle with a sine glyph
      var r = 16 * s;
      o.beginPath(); o.arc(nodes.src, y, r, 0, Math.PI * 2); stroke(C);
      o.beginPath(); for (var i = -r * 0.7; i <= r * 0.7; i += 1) { var yy = y - Math.sin(i / (r * 0.7) * Math.PI) * r * 0.45; if (i === -r * 0.7) o.moveTo(nodes.src + i, yy); else o.lineTo(nodes.src + i, yy); } stroke(T);
      label(o, 'V1', nodes.src, y + r + 12, 0.5, 9, 'center');
      wireTo(nodes.src + r, nodes.r1 - 22 * s);
      // R1: zigzag
      o.beginPath(); o.moveTo(nodes.r1 - 22 * s, y);
      for (var z = 0; z < 6; z++) o.lineTo(nodes.r1 - 22 * s + (z + 0.5) * (44 * s / 6), y + (z % 2 ? 7 : -7) * s);
      o.lineTo(nodes.r1 + 22 * s, y); stroke(C);
      if (!mobile) label(o, 'R1  10k', nodes.r1, y - 16 * s, 0.5, 9, 'center');
      wireTo(nodes.r1 + 22 * s, nodes.opamp - 26 * s);
      // op-amp: triangle with + / -
      var ox = nodes.opamp, oh = 26 * s;
      o.beginPath(); o.moveTo(ox - 26 * s, y - oh); o.lineTo(ox + 26 * s, y); o.lineTo(ox - 26 * s, y + oh); o.closePath(); stroke(C);
      label(o, '+', ox - 19 * s, y - 9 * s, 0.6, 11, 'center'); label(o, '−', ox - 19 * s, y + 9 * s, 0.6, 11, 'center');
      label(o, 'U1', ox, y - oh - 10, 0.5, 9, 'center');
      // feedback: from output up and back to the inverting input
      o.beginPath(); o.moveTo(ox + 26 * s, y); o.lineTo(ox + 40 * s, y); o.lineTo(ox + 40 * s, y + oh + 16 * s); o.lineTo(ox - 40 * s, y + oh + 16 * s); o.lineTo(ox - 40 * s, y + 9 * s); o.lineTo(ox - 26 * s, y + 9 * s); stroke('rgba(' + TEAL + ', 0.5)');
      if (!mobile) label(o, 'R2  47k', ox, y + oh + 28 * s, 0.5, 9, 'center');
      junction(ox + 40 * s);
      wireTo(ox + 40 * s, nodes.c1 - 4 * s);
      // C1 to ground
      junction(nodes.c1);
      o.beginPath(); o.moveTo(nodes.c1, y); o.lineTo(nodes.c1, y + 14 * s); o.moveTo(nodes.c1 - 9 * s, y + 14 * s); o.lineTo(nodes.c1 + 9 * s, y + 14 * s); o.moveTo(nodes.c1 - 9 * s, y + 19 * s); o.lineTo(nodes.c1 + 9 * s, y + 19 * s); stroke(C);
      ground(nodes.c1, y + 19 * s);
      if (!mobile) label(o, 'C1  100n', nodes.c1 + 12 * s, y + 16 * s, 0.5, 9);
      wireTo(nodes.c1, nodes.adc - 30 * s);
      // ADC block
      var aw = 60 * s, ah = 34 * s;
      o.strokeStyle = C; o.strokeRect(nodes.adc - aw / 2, y - ah / 2, aw, ah);
      label(o, 'ADC', nodes.adc, y - 3, 0.75, 11, 'center'); label(o, '12-BIT', nodes.adc, y + 10, 0.45, 8, 'center');
      // 8-bit bus: parallel lines fanning out then a bus stub
      var bx0 = nodes.adc + aw / 2, bx1 = nodes.mcu - 34 * s;
      for (var b = 0; b < 8; b++) {
        var by = y - 10.5 * s + b * 3 * s;
        o.beginPath(); o.moveTo(bx0, by); o.lineTo(bx1, by); o.strokeStyle = 'rgba(' + AMBER + ', 0.35)'; o.lineWidth = 1; o.stroke();
      }
      o.lineWidth = 1.5; o.strokeStyle = 'rgba(' + AMBER + ', 0.6)'; o.beginPath(); o.moveTo(bx0 + (bx1 - bx0) * 0.5 - 6, y + 16 * s); o.lineTo(bx0 + (bx1 - bx0) * 0.5 + 6, y - 16 * s); o.stroke();
      if (!mobile) label(o, 'D[7:0]', bx0 + (bx1 - bx0) * 0.5 + 10, y - 14 * s, 0.5, 9);
      // MCU block
      var mw = 68 * s, mh = 44 * s;
      o.strokeStyle = C; o.strokeRect(nodes.mcu - mw / 2, y - mh / 2, mw, mh);
      for (var p2 = 0; p2 < 6; p2++) { var py = y - mh / 2 + 6 * s + p2 * 6.4 * s; o.beginPath(); o.moveTo(nodes.mcu + mw / 2, py); o.lineTo(nodes.mcu + mw / 2 + 8 * s, py); o.stroke(); }
      label(o, 'MCU', nodes.mcu, y - 4, 0.75, 11, 'center'); label(o, 'STM32', nodes.mcu, y + 10, 0.45, 8, 'center');
      sheet = mk;
      // carriers: things that flow along the chain
      wire = [nodes.src + r, nodes.opamp - 26 * s, nodes.opamp + 26 * s, nodes.adc - aw / 2, nodes.adc + aw / 2, bx1];
      carriers = [];
    }

    function drawWave(x0, x1, amp, t, phase, col, square) {
      // small live waveform drawn above a wire segment
      var mid = (x0 + x1) / 2, w = Math.min(70 * s, (x1 - x0) * 0.7), base = y - 26 * s - amp;
      ctx.strokeStyle = col; ctx.lineWidth = 1.5; ctx.lineJoin = 'round'; ctx.beginPath();
      for (var i = 0; i <= w; i += 2) {
        var ph = (i / w) * Math.PI * 4 - t * 4 + phase;
        var v = square ? (Math.sin(ph) > 0 ? 1 : -1) * 0.8 : Math.sin(ph) + 0.25 * Math.sin(ph * 3.1);
        var yy = base - v * amp;
        if (i === 0) ctx.moveTo(mid - w / 2 + i, yy); else ctx.lineTo(mid - w / 2 + i, yy);
      }
      ctx.stroke();
    }

    return {
      setup: function () { build(); },
      frame: function (ts, dt) {
        var t = (ts - t0) / 1000;
        ctx.setTransform(1, 0, 0, 1, 0, 0); ctx.drawImage(sheet, 0, 0); ctx.setTransform(DPR, 0, 0, DPR, 0, 0);
        if (reduceMotion) return false;
        // live waveforms: small at the source, clean + bigger after the op-amp, square bits on the bus
        drawWave(wire[0], wire[1], 6 * s, t, 0, 'rgba(' + TEAL + ', 0.7)', false);
        drawWave(wire[2], wire[3], 12 * s, t, 0.3, 'rgba(' + TEAL + ', 0.9)', false);
        drawWave(wire[4], wire[5], 9 * s, t, 0, 'rgba(' + AMBER + ', 0.9)', true);
        // current carriers: teal dots on analog wires, amber bits on the bus
        if (carriers.length < (mobile ? 5 : 9) && rnd() < 0.05) carriers.push({ x: wire[0], v: 70 + rnd() * 40, born: t });
        ctx.lineCap = 'round';
        for (var i = carriers.length - 1; i >= 0; i--) {
          var c = carriers[i]; c.x += c.v * dt;
          if (c.x > nodes.mcu - 34 * s) { carriers.splice(i, 1); continue; }
          if (c.x >= wire[4]) {
            // on the bus: an amber dash on one of the 8 lines
            var lane = Math.floor(((c.born * 7) % 8 + 8) % 8), by = y - 10.5 * s + lane * 3 * s;
            ctx.strokeStyle = 'rgba(' + AMBER + ', 0.95)'; ctx.lineWidth = 2.5; ctx.lineCap = 'butt';
            ctx.beginPath(); ctx.moveTo(c.x - 8, by); ctx.lineTo(c.x, by); ctx.stroke();
          } else if (c.x < wire[3] || c.x > wire[3]) {
            // through blocks the carrier is hidden; on wires it glows
            var inside = (c.x > wire[1] && c.x < wire[2]) || (c.x > wire[3] && c.x < wire[4]);
            if (!inside) {
              var rg = ctx.createRadialGradient(c.x, y, 0, c.x, y, 7);
              rg.addColorStop(0, 'rgba(' + TEAL + ', 0.95)'); rg.addColorStop(1, 'rgba(' + TEAL + ', 0)');
              ctx.fillStyle = rg; ctx.beginPath(); ctx.arc(c.x, y, 7, 0, Math.PI * 2); ctx.fill();
            }
          }
        }
        // ADC conversion tick: the block "blinks" on the sample clock
        var blink = (Math.sin(t * 9) > 0.92) ? 0.35 : 0;
        if (blink) { ctx.fillStyle = 'rgba(' + AMBER + ', ' + blink + ')'; ctx.fillRect(nodes.adc - 30 * s, y - 17 * s, 60 * s, 34 * s); }
        return true;
      }
    };
  })();

  /* ── driver ──────────────────────────────────────────────────────── */
  var concept = style === 'scope' ? Scope : style === 'schematic' ? Schematic : Board;

  function frame(ts) {
    raf = 0; if (!visible) return;
    var dt = lastT ? Math.min(0.05, (ts - lastT) / 1000) : 0; lastT = ts;
    var again = concept.frame(ts, dt);
    if (again) raf = requestAnimationFrame(frame);
  }
  function resize() {
    var parent = canvas.parentElement;
    DPR = Math.min(2, window.devicePixelRatio || 1);
    W = parent.clientWidth || window.innerWidth; H = parent.clientHeight || 400; mobile = W < 750;
    canvas.width = Math.round(W * DPR); canvas.height = Math.round(H * DPR);
    canvas.style.width = W + 'px'; canvas.style.height = H + 'px';
    keepout = measureKeepout(mobile ? 12 : 26);
    seed = 1337; t0 = performance.now(); lastT = 0;
    concept.setup();
    if (!raf) raf = requestAnimationFrame(frame);
  }
  var rt; window.addEventListener('resize', function () { clearTimeout(rt); rt = setTimeout(resize, 150); });
  canvas.parentElement.addEventListener('mousemove', function (e) {
    var r = canvas.getBoundingClientRect(); mouse.x = e.clientX - r.left; mouse.y = e.clientY - r.top; mouse.active = true;
  });
  canvas.parentElement.addEventListener('mouseleave', function () { mouse.active = false; });
  if ('IntersectionObserver' in window) {
    new IntersectionObserver(function (e) {
      visible = e[0].isIntersecting;
      if (visible && !raf && !reduceMotion) { lastT = 0; raf = requestAnimationFrame(frame); }
    }).observe(canvas);
  }
  if (document.fonts && document.fonts.ready) document.fonts.ready.then(resize);
  resize();
})();
